import React from 'react';
import { meaning, t, type AreaStatus } from '@leo/model';
import { api, ApiError, inr } from '../../api';
import { useAsync } from '../../components';
import { AskPanel } from '../../ui/AskBox';
import { Band, Skeleton, StatusLine, Tiles, WaitingCard } from '../../ui/kit';

/** One shape for every business screen: status → overview → what LEO suggests → ask. */
export function AreaPage({ area, titleKey, children, always, suggestions, filters, controls }: { area: string; titleKey: string; children?: React.ReactNode; always?: React.ReactNode; suggestions?: React.ReactNode; filters?: Record<string, string>; controls?: React.ReactNode }) {
  const st = useAsync(() => api<AreaStatus>(`/api/v1/areas/${area}/status`), [area]);
  const s = st.data;
  const tone = s?.tone ?? 'neutral';
  return (
    <div className="page">
      <div className="page-head">
        <div><div className="xs">{t(titleKey)}</div><h1>{t(titleKey)}</h1><p className="lead">{meaning(titleKey)}</p></div>
        {controls}
      </div>
      {st.error && <div className="callout">The status line could not be computed: {st.error}</div>}
      {!s && !st.error && <Skeleton lines={2} />}
      {s && s.state !== 'waiting' && <div id="area-status"><StatusLine text={s.sentence} basis={s.basis} tone={tone} /></div>}
      {s && s.tiles.length > 0 && <Tiles tiles={s.tiles} />}
      {s && s.state === 'waiting' && <WaitingCard areaKey={titleKey} unlocks={s.unlocks ?? []} alreadyKnown={s.alreadyKnown} note={s.sentence} compact />}
      {(!s || s.state !== 'waiting') && children}
      {always}
      {suggestions && <Band title="suggests" id="suggests">{suggestions}</Band>}
      <AskPanel screen={area} filters={filters} />
    </div>
  );
}

export interface Rec { id: number; decision_type: string; title: string; predicted_impact: string | null; impact_basis: string; confidence: string; downside: string; scope: Record<string, string>; evidence: Record<string, any> }
const SNAPSHOT = typeof window !== 'undefined' && !!(window as any).__LEO_SNAPSHOT__;

/** Up to five proposed actions for this area, each approvable in place with a reason. */
export function Suggestions({ type, store, brand, limit = 5 }: { type?: string; store?: string; brand?: string; limit?: number }) {
  const list = useAsync(() => api<{ recommendations: Rec[] }>(`/api/v1/decisions?status=proposed${type ? `&type=${type}` : ''}&limit=200`), [type]);
  const [done, setDone] = React.useState<Record<number, string>>({});
  const recs = (list.data?.recommendations ?? []).filter(r => (!store || r.scope.store === store) && (!brand || r.scope.brand === brand)).slice(0, limit);
  if (list.error) return <p className="muted">Suggestions are not available just now.</p>;
  if (!list.data) return <Skeleton lines={2} />;
  if (!recs.length) return <p className="muted">Nothing proposed for this selection among the current proposals.</p>;
  return <div className="sugs">{recs.map(r => <Suggestion key={r.id} rec={r} outcome={done[r.id]} onDone={(id, what) => setDone(d => ({ ...d, [id]: what }))} />)}</div>;
}

export function Suggestion({ rec, outcome, onDone }: { rec: Rec; outcome?: string; onDone: (id: number, what: string) => void }) {
  const [reason, setReason] = React.useState(''); const [busy, setBusy] = React.useState(false); const [err, setErr] = React.useState<string | null>(null);
  const decide = async (decision: 'approve' | 'reject') => {
    setErr(null); setBusy(true);
    if (reason.trim().length < 5) { setErr('Give a reason (at least a few words) — it is kept with the decision.'); setBusy(false); return; }
    try { await api(`/api/v1/decisions/${rec.id}/decide`, { method: 'POST', body: JSON.stringify({ decision, reason }) }); onDone(rec.id, SNAPSHOT ? 'Preview — not saved' : decision === 'approve' ? t('decisions.approved') : 'Declined'); }
    catch (e) { setErr(e instanceof ApiError ? (e.code === 'FORBIDDEN' ? 'Only a steward, merchandising or the owner can decide.' : e.message) : String(e)); }
    finally { setBusy(false); }
  };
  const label = rec.decision_type === 'TRANSFER' ? t('stock.transfer') : rec.decision_type === 'MARKDOWN' ? t('stock.markdown') : rec.decision_type === 'RETURN_TO_WH' ? t('stock.return_wh') : rec.decision_type;
  return (
    <article className="sug">
      <span className="pill info">{label}</span>
      <div className="t">{rec.title}</div>
      <div className="m"><span title={meaning('predicted_impact')}>{t('predicted_impact')} <b>{rec.predicted_impact != null ? inr(Number(rec.predicted_impact)) : '—'}</b></span><span title={meaning('confidence')}>{t('confidence')} <b>{Math.round(Number(rec.confidence) * 100)}%</b></span></div>
      {err && <div className="callout">{err}</div>}
      {outcome ? <span className="pill good">{outcome}</span> : (
        <div className="acts">
          <input placeholder={t('reason') + ' (required, kept with the decision)'} value={reason} onChange={e => setReason(e.target.value)} aria-label={t('reason')} />
          <button className="btn primary" disabled={busy || reason.trim().length < 5} onClick={() => decide('approve')}>{t('approve')}</button>
          <button className="btn" disabled={busy || reason.trim().length < 5} onClick={() => decide('reject')}>{t('decline')}</button>
        </div>
      )}
    </article>
  );
}
